// ============================================================================
// Feuille du noyau : INTÉGRALES (refonte, lot 6.60, moteur v55).
// ============================================================================
// L'énoncé : une intégrale à calculer, « I = ∫(0 ; 1) x × eˣ dx ». L'élève écrit, une
// étape par ligne, des égalités qui partent de I (ou d'une intégrale de sa feuille) :
//
//   « u(x) = x », « v′(x) = eˣ », « u′(x) = 1 », « v(x) = eˣ »   nommer (facultatif)
//   « I = [x × eˣ](0 ; 1) − ∫(0 ; 1) eˣ dx »                    intégration par parties
//   « I = [x × eˣ](0 ; 1) − [eˣ](0 ; 1) »                       une primitive
//   « I = (1 × e − 0) − (e − 1) »                              remplacer x par les bornes
//   « I = 1 »                                                  calculer
//
//   Nommer u, v′ ; calculer u′, v                        1   integral.name@1
//   Intégration par parties                              2   integral.parts@1
//   Primitive (usuelle)                                  2   integral.primitive@1
//   Reconnaître u′eᵘ, u′ ÷ u, u′uⁿ, u′ ÷ u²…              3   integral.recognize@1
//   Intégrale d'une constante                            1   integral.constant@1
//   Linéarité (sortir une constante, séparer, réunir)    1   integral.linearity@1
//   Linéariser (cos², sin², cos⁴…)                       2   integral.linearize@1
//   Décomposer en éléments simples                       2   integral.decompose@1
//   Transformer la fonction à intégrer                   1   integral.rewrite@1
//   Relation de Chasles (couper l'intervalle)            2   integral.chasles@1
//   Remplacer x par les bornes (crochet)                 1   integral.bracket@1
//   Regrouper ou transformer un crochet                  1   integral.bracket_merge@1
//   Remplacer (un calcul fait à part)                    1   integral.replace@1
//   Reconnaître I (équation en I)                        1   integral.recognize_i@1
//   Regrouper les I, diviser                             1   integral.collect@1 …
//   Calculer                                             1   kernel.compute@1
//
// Une intégrale s'écrit « ∫(a ; b) … dx » : un bout « ∫(a ; b) » (le signe et ses bornes),
// la fonction, puis le bout « dx ». Un crochet : « [ … ](a ; b) ». Les valeurs se
// calculent en flottants (quadrature de Gauss-Kronrod, valeur du crochet F(b) − F(a)) ;
// deux fonctions sont égales si elles ont les mêmes valeurs en 11 points de l'intervalle
// (preuve ÉCHANTILLONNÉE, comme les dérivées du lot 5.9). Une ligne fausse ne dit jamais
// la bonne valeur ; une ligne juste qui saute des étapes reste sur la feuille sans HORA
// (D7).
// ============================================================================

import type { Chip } from './lexer';
import {
  checkSyntax,
  isConst,
  isFunc,
  isNum,
  isOperand,
  isPow,
  isSupPow,
  isVar,
  lex,
  normalize,
} from './lexer';
import type { ScratchModel } from './model';
import { atomsText, type Line } from './tree';
import {
  add,
  canon,
  div,
  mul,
  num,
  parseStatement,
  substitute,
  symbolsOf,
  termKey,
  type Term,
} from './kernel/term';
import { evalFloat, withRadianArcs } from './kernel/normal';
import {
  closeTo,
  derive,
  splitConstant,
  stepTemplate,
  termsOf,
  withArcDerivatives,
  type StepRule,
} from './kernel/calculus';
import { constOps, constShapeKey } from './kernel-formula';

export const INTEGRAL_STEP_IDS = {
  name: 'integral.name@1',
  parts: 'integral.parts@1',
  primitive: 'integral.primitive@1',
  recognize: 'integral.recognize@1',
  constant: 'integral.constant@1',
  linearity: 'integral.linearity@1',
  linearize: 'integral.linearize@1',
  decompose: 'integral.decompose@1',
  rewrite: 'integral.rewrite@1',
  chasles: 'integral.chasles@1',
  bracket: 'integral.bracket@1',
  bracketMerge: 'integral.bracket_merge@1',
  replace: 'integral.replace@1',
  recognizeI: 'integral.recognize_i@1',
  collect: 'integral.collect@1',
  divide: 'integral.divide@1',
  compute: 'kernel.compute@1',
} as const;
type StepKind = keyof typeof INTEGRAL_STEP_IDS;

const STEP: Record<StepKind, { label: string; hora: number; why: string }> = {
  name: {
    label: 'Nommer',
    hora: 1,
    why: 'On choisit u et v′ avec u × v′ = la fonction à intégrer ; u′ se dérive, v est une primitive de v′.',
  },
  parts: {
    label: 'Intégration par parties',
    hora: 2,
    why: '∫u v′ = [u v] − ∫u′ v : on dérive u, on prend une primitive de v′.',
  },
  primitive: {
    label: 'Primitive',
    hora: 2,
    why: 'Une primitive F de la fonction : ∫(a ; b) f(x) dx = [F(x)](a ; b).',
  },
  recognize: {
    label: 'Reconnaître une forme',
    hora: 3,
    why: 'La fonction est de la forme u′eᵘ, u′ ÷ u, u′uⁿ… : on écrit directement sa primitive.',
  },
  constant: {
    label: 'Intégrale d’une constante',
    hora: 1,
    why: '∫(a ; b) k dx = k × (b − a).',
  },
  linearity: {
    label: 'Linéarité',
    hora: 1,
    why: 'L’intégrale d’une somme est la somme des intégrales ; une constante sort de l’intégrale.',
  },
  linearize: {
    label: 'Linéariser',
    hora: 2,
    why: 'cos²x = (1 + cos 2x) ÷ 2, sin²x = (1 − cos 2x) ÷ 2 : plus de puissance de cos ou de sin.',
  },
  decompose: {
    label: 'Décomposer en éléments simples',
    hora: 2,
    why: 'La fraction devient une somme de fractions plus simples, qui ont chacune une primitive.',
  },
  rewrite: {
    label: 'Transformer la fonction',
    hora: 1,
    why: 'Une écriture égale de la fonction à intégrer, plus facile à intégrer.',
  },
  chasles: {
    label: 'Relation de Chasles',
    hora: 2,
    why: '∫(a ; b) = ∫(a ; c) + ∫(c ; b) : sur chaque morceau, la fonction s’écrit sans valeur absolue.',
  },
  bracket: {
    label: 'Remplacer x par les bornes',
    hora: 1,
    why: '[F(x)](a ; b) = F(b) − F(a) : la borne du haut, puis celle du bas.',
  },
  bracketMerge: {
    label: 'Regrouper les crochets',
    hora: 1,
    why: 'Des crochets aux mêmes bornes se regroupent : [F](a ; b) + [G](a ; b) = [F + G](a ; b).',
  },
  replace: {
    label: 'Remplacer',
    hora: 1,
    why: 'On remplace une intégrale par ce qu’on a calculé à part.',
  },
  recognizeI: {
    label: 'Reconnaître I',
    hora: 1,
    why: 'L’intégrale de départ réapparaît : on l’écrit I, ce qui donne une équation en I.',
  },
  collect: {
    label: 'Regrouper les I',
    hora: 1,
    why: 'On passe les I du même côté.',
  },
  divide: {
    label: 'Diviser',
    hora: 1,
    why: 'On divise les deux membres par le coefficient de I.',
  },
  compute: {
    label: 'Calculer',
    hora: 1,
    why: 'Un calcul sur les nombres.',
  },
};

const CARD = 'Intégrales';

export interface KIntVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'building'
    | 'ready'
    | 'pending'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'unjustified'
    | 'useless'
    | 'step';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  /** D7 : ligne juste qui saute des étapes, gardée sans HORA */
  jump?: boolean;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  imove?: IMove;
}

/** ce que la ligne apprend au moteur */
interface IMove {
  text: string;
  /** les membres établis : dans la classe k (k·I), ou à côté d'une intégrale */
  to: { k: 'class'; key: string; forms: Form[] } | { k: 'side'; key: string; forms: Form[] } | null;
  /** une ligne qui nomme u, v′, u′ ou v */
  name?: { head: NameHead; expr: Term };
  final?: boolean;
}

type NameHead = 'u' | 'du' | 'dv' | 'v';

export interface KIntState {
  model: ScratchModel;
  /** le nom de l'intégrale (« I », « I₃ ») */
  name: string;
  /** la variable d'intégration (« x ») */
  x: string;
  /** l'intégrale de départ */
  startAtom: Atom;
  value: number;
  answer: Term;
  answerValue: number;
  answerOps: number;
  /** les écritures établies de k·I (clé de k) */
  classes: Map<string, Form[]>;
  /** les écritures établies d'une intégrale ou d'un crochet, calculé à part */
  side: Map<string, Form[]>;
  names: Partial<Record<NameHead, Term>>;
  written: Set<string>;
  givenTexts: Set<string>;
  path: string[];
  traps: { text: string; line: Line; title: string; why: string }[];
  lines: number;
  concluded: boolean;
  cache: Map<string, number>;
}

export class IntegralModelError extends Error {}

/* ------------------------------ les bouts ------------------------------ */

const INT_RE = /^∫\((.+) ; (.+)\)$/;
const BRK_RE = /^\]\((.+) ; (.+)\)$/;
const DIFF_RE = /^d([a-zθ])$/;
const NAME_RE = /^([uv])(′?)\(([a-zθ])\)$/;

export const isIntegralAtom = (a: string | undefined): boolean => !!a && INT_RE.test(a);
export const isBracketClose = (a: string | undefined): boolean => !!a && BRK_RE.test(a);
export const isDiffAtom = (a: string | undefined): boolean => !!a && DIFF_RE.test(a);
const isSpecial = (a: string | undefined): boolean =>
  isIntegralAtom(a) || isBracketClose(a) || isDiffAtom(a) || a === '[';

/**
 * Les bornes d'un bout « ∫(a ; b) » ou « ](a ; b) » (affichage : ∫ ou ] avec a en bas et b
 * en haut) ; null pour un autre bout.
 */
export function integralBoundsOf(
  a: string | undefined,
): { kind: 'int' | 'brk'; lo: string; hi: string } | null {
  if (!a) return null;
  const m = INT_RE.exec(a) ?? BRK_RE.exec(a);
  if (!m) return null;
  return { kind: a.startsWith('∫') ? 'int' : 'brk', lo: m[1]!, hi: m[2]! };
}

/** une borne écrite comme le moteur la relit (« π ÷ 2 », « e² ») */
function boundText(s: string): string {
  return exprText(normalize(lex(s.trim())));
}
function exprText(atoms: string[]): string {
  return atomsText(atoms)
    .replace(/\//g, '÷')
    .replace(/\s*÷\s*/g, ' ÷ ')
    .trim();
}
/** « ∫(0 ; π ÷ 2) » */
export function integralAtom(lo: string, hi: string): string {
  return `∫(${boundText(lo)} ; ${boundText(hi)})`;
}
/** « ](0 ; π ÷ 2) » */
export function bracketCloseAtom(lo: string, hi: string): string {
  return `](${boundText(lo)} ; ${boundText(hi)})`;
}

/** le contenu d'un groupe entre parenthèses qui commence en `open` (indice du « ( ») */
function group(text: string, open: number): { inner: string; end: number } | null {
  let depth = 0;
  for (let i = open; i < text.length; i++) {
    const c = text[i]!;
    if (c === '(') depth++;
    else if (c === ')') {
      depth--;
      if (depth === 0) return { inner: text.slice(open + 1, i), end: i + 1 };
    }
  }
  return null;
}
function splitBounds(inner: string): [string, string] | null {
  let depth = 0;
  for (let i = 0; i < inner.length; i++) {
    const c = inner[i]!;
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === ';' && depth === 0) return [inner.slice(0, i), inner.slice(i + 1)];
  }
  return null;
}

const LETTER_RE = /[A-Za-zÀ-ÖØ-öø-ÿα-ωΑ-Ω]/;

/** Une ligne (texte) en bouts : « ∫(0 ; 1) », « dx », « [ », « ](0 ; 1) », puis comme partout. */
export function kintLineAtoms(text: string): string[] {
  const out: string[] = [];
  let buf = '';
  const flush = () => {
    if (buf.trim()) out.push(...lex(buf));
    buf = '';
  };
  const s = String(text);
  for (let i = 0; i < s.length; ) {
    const c = s[i]!;
    if (c === '∫' && s[i + 1] === '(') {
      const g = group(s, i + 1);
      const b = g && splitBounds(g.inner);
      if (g && b) {
        flush();
        out.push(integralAtom(b[0], b[1]));
        i = g.end;
        continue;
      }
    }
    if (c === '[') {
      flush();
      out.push('[');
      i++;
      continue;
    }
    if (c === ']' && s[i + 1] === '(') {
      const g = group(s, i + 1);
      const b = g && splitBounds(g.inner);
      if (g && b) {
        flush();
        out.push(bracketCloseAtom(b[0], b[1]));
        i = g.end;
        continue;
      }
    }
    if (
      c === 'd' &&
      /[a-zθ]/.test(s[i + 1] ?? '') &&
      !LETTER_RE.test(s[i - 1] ?? '') &&
      !/[A-Za-zÀ-ÿα-ω₀-₉′(]/.test(s[i + 2] ?? '')
    ) {
      flush();
      out.push('d' + s[i + 1]);
      i += 2;
      continue;
    }
    buf += c;
    i++;
  }
  flush();
  return kintNormalize(wrapArgs(out)).atoms;
}

/** « exp x » (écrit « e^x ») : l'argument entre parenthèses, comme sur la palette */
function wrapArgs(atoms: string[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < atoms.length; i++) {
    const a = atoms[i]!;
    out.push(a);
    const n = atoms[i + 1];
    if (
      isFunc(a) &&
      n !== undefined &&
      n !== '(' &&
      (isVar(n) || isNum(n) || isConst(n)) &&
      !isSpecial(n)
    ) {
      out.push('(', n, ')');
      i++;
    }
  }
  return out;
}

const endsOp = (a: string): boolean =>
  isOperand(a) || a === ')' || a === '²' || isSupPow(a) || a === '!' || isPow(a);
const startsOp = (a: string): boolean =>
  isVar(a) || isConst(a) || isFunc(a) || a === '(' || a === '√' || isNum(a);

/** Multiplication sous-entendue, sans jamais en mettre contre « dx » ni dans « ∫( » . */
export function kintNormalize(atoms: string[]): { atoms: string[]; src: number[] } {
  const out: string[] = [];
  const src: number[] = [];
  atoms.forEach((a, i) => {
    const p = out[out.length - 1];
    if (p !== undefined && p !== '×') {
      const pSpecialOpen = isIntegralAtom(p) || p === '[';
      const pCloses = isBracketClose(p) || isDiffAtom(p);
      let times = false;
      if (isIntegralAtom(a) || a === '[') times = !pSpecialOpen && (pCloses || endsOp(p));
      else if (isBracketClose(a) || isDiffAtom(a)) times = false;
      else if (pCloses) times = startsOp(a) && !isNum(a) ? true : isNum(a);
      else if (!pSpecialOpen) times = endsOp(p) && startsOp(a) && !isNum(a);
      if (times) {
        out.push('×');
        src.push(i);
      }
    }
    out.push(a);
    src.push(i);
  });
  return { atoms: out, src };
}

/** L'écriture d'une ligne (enregistrement, comparaison). */
export function kintText(atoms: string[]): string {
  const parts: string[] = [];
  let run: string[] = [];
  const flush = () => {
    if (run.length) parts.push(exprText(run));
    run = [];
  };
  for (const a of atoms) {
    if (isSpecial(a)) {
      flush();
      parts.push(a);
    } else run.push(a);
  }
  flush();
  let out = '';
  parts.forEach((p, i) => {
    const prev = parts[i - 1];
    if (!i) out = p;
    else if (prev === '[' || isBracketClose(p)) out += p;
    else out += ' ' + p;
  });
  return out.replace(/\s+/g, ' ').trim();
}

const lineOf = (atoms: string[]): Line => ({
  dis: [{ sides: [{ k: 's', n: kintText(atoms) }] }],
});
/** Ligne de feuille (texte) pour l'app. */
export function kintLine(text: string): Line {
  return lineOf(kintLineAtoms(text));
}

/* ------------------------------ termes ------------------------------ */

const E: Term = { k: 'fn', f: 'exp', a: { k: 'num', v: { n: 1n, d: 1n } } } as Term;
/** la constante e s'écrit « e » ; « e^(…) » est déjà lu exp(…) */
function withE(t: Term): Term {
  switch (t.k) {
    case 'sym':
      return t.n === 'e' ? E : t;
    case 'add':
    case 'mul':
      return { ...t, c: t.c.map(withE) } as Term;
    case 'div':
    case 'pow':
    case 'binom':
      return { ...t, a: withE(t.a), b: withE(t.b) } as Term;
    case 'fn':
      return { ...t, a: withE(t.a) } as Term;
    default:
      return t;
  }
}

/** une expression seule (null : illisible) */
function exprOf(atoms: string[]): Term | null {
  if (!atoms.length) return null;
  try {
    const p = parseStatement(atomsText(atoms));
    return p.ok && p.s.k === 'expr' ? withE(p.s.e) : null;
  } catch {
    return null;
  }
}
function exprOfText(text: string): Term | null {
  try {
    return exprOf(normalize(lex(text)));
  } catch {
    return null;
  }
}

const keyOf = (t: Term): string => {
  try {
    return termKey(canon(t));
  } catch {
    return termKey(t);
  }
};
const shape = (t: Term): string => {
  try {
    return constShapeKey(t);
  } catch {
    return termKey(t);
  }
};
/** le coefficient numérique entier en tête d'une écriture (6π : 6 ; 3 : 3) */
function leadInt(t: Term): bigint | null {
  if (t.k === 'num') return t.v.d === 1n ? (t.v.n < 0n ? -t.v.n : t.v.n) : null;
  if (t.k === 'mul' && t.c[0]?.k === 'num') return leadInt(t.c[0]);
  return null;
}
const gcdB = (a: bigint, b: bigint): bigint => (b === 0n ? a : gcdB(b, a % b));
/** les fractions qui se simplifient encore (6π ÷ 8) : un calcul de plus à faire */
function reducible(t: Term): number {
  switch (t.k) {
    case 'div': {
      const a = leadInt(t.a);
      const b = leadInt(t.b);
      const own = a !== null && b !== null && a > 0n && b > 1n && gcdB(a, b) > 1n ? 1 : 0;
      return own + reducible(t.a) + reducible(t.b);
    }
    case 'add':
    case 'mul':
      return t.c.reduce((s, c) => s + reducible(c), 0);
    case 'pow':
      return reducible(t.a) + reducible(t.b);
    case 'fn':
      return reducible(t.a);
    default:
      return 0;
  }
}
const opsOf = (t: Term): number => {
  try {
    return constOps(t) + reducible(t);
  } catch {
    return 99;
  }
};
const isOne = (t: Term): boolean => t.k === 'num' && t.v.n === 1n && t.v.d === 1n;
const isZeroT = (t: Term): boolean => t.k === 'num' && t.v.n === 0n;

function val(t: Term, env: Record<string, number> = {}): number {
  try {
    // moteur v60 : arctan, arcsin, arccos en RADIANS (le calcul du noyau parle en degrés)
    const v = withRadianArcs(() => evalFloat(t, env));
    return Number.isFinite(v) ? v : NaN;
  } catch {
    return NaN;
  }
}
const near = (a: number, b: number): boolean =>
  Number.isFinite(a) &&
  Number.isFinite(b) &&
  Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));

/**
 * Les calculs « immédiats » faits : × 1, × 0, + 0, ÷ 1, a¹, a⁰, 1ⁿ, et une fonction d'une
 * constante qui vaut −1, 0 ou 1 (ln 1, ln e, e⁰, sin π, cos 0…). Ils ne comptent pas dans
 * la taille d'un calcul : remplacer x par les bornes puis les faire n'est qu'un pas.
 */
function trivial(t: Term): Term {
  switch (t.k) {
    case 'fn': {
      const a = trivial(t.a);
      const r: Term = { ...t, a };
      if ([...symbolsOf(a)].every((s) => s === 'π')) {
        const v = val(r);
        for (const k of [-1, 0, 1])
          if (Number.isFinite(v) && Math.abs(v - k) < 1e-12) return num(k);
      }
      return r;
    }
    case 'mul': {
      const c = t.c.map(trivial);
      if (c.some(isZeroT)) return num(0);
      const kept = c.filter((f) => !isOne(f));
      return kept.length === 0 ? num(1) : kept.length === 1 ? kept[0]! : mul(...kept);
    }
    case 'add': {
      const c = t.c.map(trivial).filter((f) => !isZeroT(f));
      return c.length === 0 ? num(0) : c.length === 1 ? c[0]! : add(...c);
    }
    case 'div': {
      const a = trivial(t.a);
      const b = trivial(t.b);
      if (isZeroT(a)) return num(0);
      return isOne(b) ? a : div(a, b);
    }
    case 'pow': {
      const a = trivial(t.a);
      const b = trivial(t.b);
      if (isOne(b)) return a;
      if (isZeroT(b) || isOne(a)) return num(1);
      if (isZeroT(a)) return num(0);
      return { ...t, a, b };
    }
    default:
      return t;
  }
}

/* ------------------------------ quadrature ------------------------------ */

const XGK = [
  0.9914553711208126, 0.9491079123427585, 0.8648644233597691, 0.7415311855993945,
  0.5860872354676911, 0.4058451513773972, 0.20778495500789848, 0,
];
const WGK = [
  0.022935322010529224, 0.06309209262997856, 0.10479001032225019, 0.14065325971552592,
  0.1690047266392679, 0.19035057806478542, 0.20443294007529889, 0.20948214108472782,
];
const WG = [0.1294849661688697, 0.27970539148927664, 0.3818300505051189, 0.4179591836734694];

function gk15(f: (x: number) => number, a: number, b: number): { k: number; err: number } {
  const c = (a + b) / 2;
  const h = (b - a) / 2;
  const fc = f(c);
  let k = fc * WGK[7]!;
  let g = fc * WG[3]!;
  for (let j = 0; j < 7; j++) {
    const dx = h * XGK[j]!;
    const s = f(c - dx) + f(c + dx);
    k += WGK[j]! * s;
    if (j % 2 === 1) g += WG[(j - 1) / 2]! * s;
  }
  return { k: k * h, err: Math.abs((k - g) * h) };
}

/** ∫(a ; b) f, quadrature adaptative (NaN si f n'a pas de valeur) */
export function integrateNum(f: (x: number) => number, a: number, b: number): number {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return NaN;
  if (a === b) return 0;
  const whole = gk15(f, a, b);
  if (!Number.isFinite(whole.k)) return NaN;
  const tol = 1e-13 * Math.max(1, Math.abs(whole.k));
  let evals = 0;
  const rec = (lo: number, hi: number, r: { k: number; err: number }, depth: number): number => {
    if (r.err <= tol * ((hi - lo) / (b - a) + 1e-3) || depth > 40 || evals > 4000) return r.k;
    const m = (lo + hi) / 2;
    const left = gk15(f, lo, m);
    const right = gk15(f, m, hi);
    evals += 2;
    return rec(lo, m, left, depth + 1) + rec(m, hi, right, depth + 1);
  };
  const v = rec(a, b, whole, 0);
  return Number.isFinite(v) ? v : NaN;
}

/* ------------------------------ intégrales, crochets, écritures ------------------------------ */

interface Atom {
  k: 'int' | 'brk';
  lo: Term;
  hi: Term;
  a: number;
  b: number;
  body: Term;
  key: string;
  /** l'écriture (« ∫(0 ; 1) x × exp(x) dx », « [x × exp(x)](0 ; 1) ») */
  text: string;
}

interface Item {
  /** null : le nom de l'intégrale (I) */
  atom: Atom | null;
  coef: Term;
  cv: number;
}

interface Form {
  items: Item[];
  c: Term;
  cv: number;
  /** coefficient de I */
  iCoef: number;
  /** écriture (sommes et produits triés, nombres tels qu'écrits) */
  wkey: string;
}

const itemKey = (it: Item): string => (it.atom ? it.atom.key : 'I');

function atomKey(k: 'int' | 'brk', lo: Term, hi: Term, body: Term): string {
  return `${k}|${keyOf(lo)}|${keyOf(hi)}|${keyOf(body)}`;
}

function atomValue(ks: KIntState, at: Atom): number {
  const hit = ks.cache.get(at.key);
  if (hit !== undefined) return hit;
  let v: number;
  if (at.k === 'brk') v = val(at.body, { [ks.x]: at.b }) - val(at.body, { [ks.x]: at.a });
  else v = integrateNum((p) => val(at.body, { [ks.x]: p }), at.a, at.b);
  ks.cache.set(at.key, v);
  return v;
}

function formValue(ks: KIntState, f: Form, iValue = ks.value): number {
  let v = f.cv;
  for (const it of f.items) v += it.cv * (it.atom ? atomValue(ks, it.atom) : iValue);
  return v;
}

function formOps(f: Form, triv = false): number {
  const o = (t: Term): number => opsOf(triv ? trivial(t) : t);
  let n = o(f.c);
  for (const it of f.items) if (!isOne(it.coef)) n += o(it.coef);
  return n;
}

type Summand = { coef: Term; ref: string | null };

/** l'écriture, linéaire en les intégrales, crochets et I (null : pas linéaire) */
function linear(t: Term, refs: Set<string>): Summand[] | null {
  const has = (u: Term): boolean => [...symbolsOf(u)].some((s) => refs.has(s));
  if (!has(t)) return [{ coef: t, ref: null }];
  switch (t.k) {
    case 'sym':
      return [{ coef: num(1), ref: t.n }];
    case 'add': {
      const out: Summand[] = [];
      for (const c of t.c) {
        const l = linear(c, refs);
        if (!l) return null;
        out.push(...l);
      }
      return out;
    }
    case 'mul': {
      const vary = t.c.filter(has);
      if (vary.length !== 1) return null;
      const consts = t.c.filter((c) => !has(c));
      const l = linear(vary[0]!, refs);
      if (!l) return null;
      return l.map((s) => ({
        coef: consts.length
          ? isOne(s.coef)
            ? consts.length === 1
              ? consts[0]!
              : mul(...consts)
            : mul(...consts, s.coef)
          : s.coef,
        ref: s.ref,
      }));
    }
    case 'div': {
      if (has(t.b)) return null;
      const l = linear(t.a, refs);
      if (!l) return null;
      return l.map((s) => ({ coef: div(s.coef, t.b), ref: s.ref }));
    }
    default:
      return null;
  }
}

function buildForm(sums: Summand[], atoms: Map<string, Atom>, name: string): Form {
  const groups = new Map<string, { atom: Atom | null; coefs: Term[] }>();
  const consts: Term[] = [];
  for (const s of sums) {
    if (s.ref === null) {
      consts.push(s.coef);
      continue;
    }
    const atom = s.ref === name ? null : atoms.get(s.ref)!;
    const k = atom ? atom.key : 'I';
    const g = groups.get(k) ?? { atom, coefs: [] };
    g.coefs.push(s.coef);
    groups.set(k, g);
  }
  const items: Item[] = [...groups.values()]
    .map((g) => {
      const coef = g.coefs.length === 1 ? g.coefs[0]! : add(...g.coefs);
      return { atom: g.atom, coef, cv: val(coef) };
    })
    .filter((it) => !near(it.cv, 0) || !Number.isFinite(it.cv));
  const c = consts.length === 0 ? num(0) : consts.length === 1 ? consts[0]! : add(...consts);
  const iCoef = items.filter((it) => !it.atom).reduce((s, it) => s + it.cv, 0);
  const wkey =
    items
      .map((it) => `${itemKey(it)}:${shape(it.coef)}`)
      .sort()
      .join(',') +
    '#' +
    (isZeroT(c) ? '' : shape(c));
  return { items, c, cv: val(c), iCoef, wkey };
}

/* ------------------------------ lecture d'une ligne ------------------------------ */

type ReadResult =
  | { k: 'empty' }
  | { k: 'incomplete' }
  | { k: 'syntax'; title: string; message: string; index: number }
  | { k: 'noeq' }
  | { k: 'name'; head: NameHead; expr: Term }
  | { k: 'chain'; members: Form[] };

const syntax = (title: string, message: string, index: number): ReadResult => ({
  k: 'syntax',
  title,
  message,
  index,
});

/** Les membres d'une ligne : intégrales et crochets remplacés par des noms, puis lus. */
function readLine(ks: KIntState, A: string[]): ReadResult {
  if (!A.length) return { k: 'empty' };
  // nommer : « u(x) = x », « v′(x) = exp(x) »
  const nm = NAME_RE.exec(A[0]!);
  if (nm) {
    if (A.length === 1) return { k: 'incomplete' };
    if (A[1] !== '=') return syntax('Nomme une fonction', 'Écris « u(x) = … ».', 1);
    if (nm[3] !== ks.x)
      return syntax(
        'La variable',
        `Écris ${nm[1]}${nm[2]}(${ks.x}) = … : la variable est ${ks.x}.`,
        0,
      );
    const rest = A.slice(2);
    if (!rest.length) return { k: 'incomplete' };
    if (rest.some(isSpecial) || rest.includes('='))
      return syntax(
        'Une fonction',
        'Après « = », écris seulement une fonction de ' + ks.x + '.',
        2,
      );
    const syn = checkSyntax(rest);
    if (!syn.ok) return syntax('Ça commence mal', syn.msg, 2 + syn.index);
    if (!syn.complete) return { k: 'incomplete' };
    const e = exprOf(rest);
    if (!e) return syntax('Ça ne se lit pas', 'Vérifie les parenthèses.', A.length - 1);
    const head: NameHead = nm[1] === 'u' ? (nm[2] ? 'du' : 'u') : nm[2] ? 'dv' : 'v';
    return { k: 'name', head, expr: e };
  }
  // les intégrales et les crochets
  const atoms = new Map<string, Atom>();
  const outer: string[] = [];
  const outerSrc: number[] = [];
  let n = 0;
  for (let i = 0; i < A.length; i++) {
    const a = A[i]!;
    if (isIntegralAtom(a) || a === '[') {
      const int = isIntegralAtom(a);
      let j = i + 1;
      while (j < A.length && !(int ? isDiffAtom(A[j]) : isBracketClose(A[j]))) {
        if (isSpecial(A[j]))
          return syntax(
            int ? 'Ferme l’intégrale' : 'Ferme le crochet',
            int
              ? 'Une intégrale se ferme par « dx » avant d’écrire autre chose.'
              : 'Un crochet se ferme par « ](a ; b) » avant d’écrire autre chose.',
            j,
          );
        j++;
      }
      if (j >= A.length) return { k: 'incomplete' };
      const body = A.slice(i + 1, j).filter(
        (x, k, arr) => !(x === '×' && (k === 0 || k === arr.length - 1)),
      );
      if (!body.length)
        return syntax(
          int ? 'La fonction à intégrer' : 'Dans le crochet',
          int ? 'Écris la fonction entre ∫ et dx.' : 'Écris une fonction entre les crochets.',
          j,
        );
      const syn = checkSyntax(normalize(body));
      if (!syn.ok)
        return syntax('Ça commence mal', syn.msg, i + 1 + Math.min(syn.index, body.length - 1));
      if (!syn.complete) return syntax('Ça ne se lit pas', 'La fonction n’est pas finie.', j);
      const fx = exprOf(normalize(body));
      if (!fx) return syntax('Ça ne se lit pas', 'Vérifie les parenthèses.', j);
      const m = (int ? INT_RE : BRK_RE).exec(int ? a : A[j]!)!;
      if (int && DIFF_RE.exec(A[j]!)![1] !== ks.x)
        return syntax(
          'La variable',
          `La variable est ${ks.x} : ferme l’intégrale par « d${ks.x} ».`,
          j,
        );
      const lo = exprOfText(m[1]!);
      const hi = exprOfText(m[2]!);
      if (!lo || !hi) return syntax('Les bornes', 'Une borne ne se lit pas.', int ? i : j);
      const at: Atom = {
        k: int ? 'int' : 'brk',
        lo,
        hi,
        a: val(lo),
        b: val(hi),
        body: fx,
        key: atomKey(int ? 'int' : 'brk', lo, hi, fx),
        text: int
          ? `${a} ${exprText(normalize(body))} ${A[j]}`
          : `[${exprText(normalize(body))}${A[j]}`,
      };
      const ph = `ψ${String(++n)
        .split('')
        .map((d) => '₀₁₂₃₄₅₆₇₈₉'[Number(d)])
        .join('')}`;
      atoms.set(ph, at);
      outer.push(ph);
      outerSrc.push(i);
      i = j;
      continue;
    }
    if (isBracketClose(a))
      return syntax('Ouvre le crochet', 'Un crochet s’ouvre par « [ » avant de se fermer.', i);
    if (isDiffAtom(a))
      return syntax(
        '« dx » ferme une intégrale',
        'Écris d’abord « ∫(a ; b) », puis la fonction.',
        i,
      );
    outer.push(a);
    outerSrc.push(i);
  }
  const O = normalize(outer);
  const syn = checkSyntax(O, { chains: true });
  if (!syn.ok)
    return syntax(
      'Ça commence mal',
      syn.msg,
      outerSrc[Math.min(syn.index, outerSrc.length - 1)] ?? 0,
    );
  if (!syn.complete) return { k: 'incomplete' };
  if (!O.includes('=')) return { k: 'noeq' };
  const refs = new Set([...atoms.keys(), ks.name]);
  const parts: string[][] = [[]];
  let depth = 0;
  for (const a of O) {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && a === '=') parts.push([]);
    else parts[parts.length - 1]!.push(a);
  }
  const members: Form[] = [];
  for (const p of parts) {
    const t = exprOf(p);
    if (!t)
      return syntax(
        'Ça ne se lit pas',
        'Un membre ne se lit pas : vérifie les parenthèses.',
        A.length - 1,
      );
    if ([...symbolsOf(t)].some((s) => !refs.has(s) && s !== 'π'))
      return syntax(
        'Une lettre en trop',
        `Hors d’une intégrale ou d’un crochet, ${ks.x} n’a pas de valeur : écris des nombres, ${ks.name}, des intégrales ou des crochets.`,
        A.length - 1,
      );
    const l = linear(t, refs);
    if (!l)
      return syntax(
        'Une intégrale à part',
        'Une intégrale (ou un crochet) se multiplie par un nombre, s’ajoute ou se retranche ; elle ne se met pas dans une fonction.',
        A.length - 1,
      );
    members.push(buildForm(l, atoms, ks.name));
  }
  return { k: 'chain', members };
}

/* ------------------------------ fonctions comparées ------------------------------ */

const RATIOS = [0.061, 0.137, 0.229, 0.318, 0.411, 0.503, 0.597, 0.689, 0.773, 0.857, 0.941];

function pointsOf(a: number, b: number): number[] {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  if (!(hi > lo) || !Number.isFinite(lo) || !Number.isFinite(hi)) return [];
  return RATIOS.map((r) => lo + r * (hi - lo));
}
function vec(t: Term, x: string, pts: number[]): number[] {
  return pts.map((p) => val(t, { [x]: p }));
}
function sameVec(u: number[], v: number[]): boolean {
  let ok = 0;
  for (let i = 0; i < u.length; i++) {
    const a = u[i]!;
    const b = v[i]!;
    if (Number.isNaN(a) !== Number.isNaN(b)) return false;
    if (Number.isNaN(a)) continue;
    if (!closeTo(a, b) && !(Math.abs(a - b) < 1e-11)) return false;
    ok++;
  }
  return ok >= 5;
}
const sameFn = (f: Term, g: Term, x: string, pts: number[]): boolean =>
  sameVec(vec(f, x, pts), vec(g, x, pts));
/** f − g est constant sur l'intervalle */
function offsetFn(f: Term, g: Term, x: string, pts: number[]): boolean {
  const u = vec(f, x, pts);
  const v = vec(g, x, pts);
  let k: number | null = null;
  let ok = 0;
  for (let i = 0; i < u.length; i++) {
    if (!Number.isFinite(u[i]!) || !Number.isFinite(v[i]!)) {
      if (Number.isNaN(u[i]!) !== Number.isNaN(v[i]!)) return false;
      continue;
    }
    const d = u[i]! - v[i]!;
    if (k === null) k = d;
    else if (!closeTo(d + 1, k + 1)) return false;
    ok++;
  }
  return ok >= 5;
}
function safeDerive(t: Term, x: string): Term | null {
  try {
    // moteur v60 : arctan, arcsin, arccos se dérivent (en radians)
    return withArcDerivatives(() => derive(t, x));
  } catch {
    return null;
  }
}

/* ------------------------------ verdicts ------------------------------ */

type Base = { atoms: string[]; line: Line };
type G =
  | { k: 'step'; steps: StepKind[]; labels: string[] }
  | { k: 'same' }
  /** `luck` : même valeur par hasard, pas la même fonction (la ligne ne sert pas de départ) */
  | { k: 'jump'; message?: string; luck?: boolean };

function stepVerdict(
  base: Base,
  steps: StepKind[],
  labels: string[],
  imove: IMove,
  final: boolean,
): KIntVerdict {
  const hora = steps.reduce((s, k) => s + STEP[k].hora, 0);
  let main = 0;
  steps.forEach((k, i) => {
    if (STEP[k].hora > STEP[steps[main]!].hora) main = i;
  });
  const shown = [...new Set(labels.map((l, i) => l || STEP[steps[i]!].label))];
  const why =
    [...new Set(steps)].map((k) => STEP[k].why).join(' ') + (final ? ' C’est la réponse.' : '');
  return {
    kind: 'step',
    ...base,
    hora,
    final,
    rule: { id: INTEGRAL_STEP_IDS[steps[main]!], card: CARD, label: shown.join(' → '), hora, why },
    imove: { ...imove, ...(final ? { final } : {}) },
  };
}

function jumpVerdict(base: Base, imove: IMove, final: boolean, message?: string): KIntVerdict {
  return {
    kind: 'useless',
    ...base,
    jump: true,
    final,
    title: final ? 'Juste !' : 'Juste, mais tu sautes des étapes',
    message:
      message ??
      (final
        ? 'C’est la bonne valeur. Détaille ton calcul, une étape par ligne, pour gagner des HORA. Tu peux garder cette ligne sans HORA.'
        : 'Ta ligne est juste. Détaille-la, une étape à la fois, pour gagner des HORA. Tu peux la garder sans HORA.'),
    imove: { ...imove, ...(final ? { final } : {}) },
  };
}

function trapOr(ks: KIntState, base: Base, why: { title: string; message: string }): KIntVerdict {
  const text = kintText(base.atoms);
  const t = ks.traps.find((t) => t.text === text);
  if (t)
    return {
      kind: 'trap',
      ...base,
      title: t.title,
      message: t.why,
      trap: { line: t.line, title: t.title, why: t.why },
    };
  return { kind: 'false', ...base, ...why };
}

/* ------------------------------ état ------------------------------ */

const classKey = (k: number): string => `k:${Math.round(k * 1e9)}`;

/** l'intégrale de la question et sa valeur */
export function integralQuestionOf(model: ScratchModel): {
  name: string;
  x: string;
  atom: Atom;
  answer: Term;
} {
  const spec = model.kernel!;
  const name = spec.goal.target;
  const A = kintLineAtoms(spec.start);
  if (A[0] !== name || A[1] !== '=')
    throw new IntegralModelError(`Le départ s’écrit « ${name} = ∫(a ; b) … dx ».`);
  const i = A.findIndex(isIntegralAtom);
  const j = A.findIndex(isDiffAtom);
  if (i !== 2 || j !== A.length - 1)
    throw new IntegralModelError(
      `Le départ s’écrit « ${name} = ∫(a ; b) … dx » : une seule intégrale.`,
    );
  const x = DIFF_RE.exec(A[j]!)![1]!;
  const m = INT_RE.exec(A[i]!)!;
  const body = exprOf(normalize(A.slice(i + 1, j)));
  const lo = exprOfText(m[1]!);
  const hi = exprOfText(m[2]!);
  if (!body || !lo || !hi) throw new IntegralModelError(`Départ illisible : « ${spec.start} ».`);
  if ([...symbolsOf(body)].some((s) => s !== x && s !== 'π'))
    throw new IntegralModelError(`La fonction à intégrer ne dépend que de ${x}.`);
  const atom: Atom = {
    k: 'int',
    lo,
    hi,
    a: val(lo),
    b: val(hi),
    body,
    key: atomKey('int', lo, hi, body),
    text: kintText(A.slice(2)),
  };
  const ans = kintLineAtoms(spec.answer);
  if (ans[0] !== name || ans[1] !== '=' || ans.length < 3)
    throw new IntegralModelError(`La réponse s’écrit « ${name} = valeur ».`);
  if (ans.slice(2).some(isSpecial))
    throw new IntegralModelError('La réponse est une valeur, sans intégrale ni crochet.');
  const answer = exprOf(ans.slice(2));
  if (!answer || [...symbolsOf(answer)].some((s) => s !== 'π'))
    throw new IntegralModelError('La réponse est une valeur (des nombres, e, π, ln…).');
  return { name, x, atom, answer };
}

export function createKIntState(model: ScratchModel): KIntState {
  const spec = model.kernel!;
  const q = integralQuestionOf(model);
  const ks: KIntState = {
    model,
    name: q.name,
    x: q.x,
    startAtom: q.atom,
    value: NaN,
    answer: q.answer,
    answerValue: val(q.answer),
    answerOps: opsOf(q.answer),
    classes: new Map(),
    side: new Map(),
    names: {},
    written: new Set(),
    givenTexts: new Set(),
    path: spec.path ?? [],
    traps: [],
    lines: 0,
    concluded: false,
    cache: new Map(),
  };
  ks.value = atomValue(ks, q.atom);
  if (!Number.isFinite(ks.value))
    throw new IntegralModelError(
      'L’intégrale n’a pas de valeur (la fonction n’est pas définie sur l’intervalle).',
    );
  const startForm: Form = {
    items: [{ atom: q.atom, coef: num(1), cv: 1 }],
    c: num(0),
    cv: 0,
    iCoef: 0,
    wkey: `${q.atom.key}:1#`,
  };
  ks.classes.set(classKey(1), [startForm]);
  ks.givenTexts = new Set(integralGivenTexts(model).map((t) => kintText(kintLineAtoms(t))));
  ks.traps = (model.traps ?? []).map((t) => ({
    text: kintText(kintLineAtoms(t.line)),
    line: kintLine(t.line),
    title: t.title ?? 'Attention',
    why: t.why,
  }));
  return ks;
}

/** les lignes de l'énoncé : l'intégrale à calculer */
export function integralGivenTexts(model: ScratchModel): string[] {
  return [model.kernel!.start, ...(model.givens ?? [])];
}

/* ------------------------------ les gestes ------------------------------ */

interface Diff {
  /** retirés (dans S, pas dans T) */
  gone: Item[];
  /** apparus */
  came: Item[];
  /** même intégrale, coefficient écrit autrement (même valeur) */
  recoef: boolean;
}

function diffForms(S: Form, T: Form): Diff {
  const left = [...T.items];
  const gone: Item[] = [];
  let recoef = false;
  for (const it of S.items) {
    const j = left.findIndex((o) => itemKey(o) === itemKey(it) && near(o.cv, it.cv));
    if (j >= 0) {
      if (shape(left[j]!.coef) !== shape(it.coef)) recoef = true;
      left.splice(j, 1);
    } else gone.push(it);
  }
  return { gone, came: left, recoef };
}

const sameBounds = (a: Atom, b: Atom): boolean => near(a.a, b.a) && near(a.b, b.b);

/** la règle de la primitive : usuelle (2) ou une forme à reconnaître (3) ; null : produit */
/**
 * Une écriture préparée pour lire sa règle de dérivation : les constantes en facteur sont
 * distribuées et les produits dépliés ((x² − 1) × eˣ² ÷ 2 : (1 ÷ 2) × (x² − 1) × eˣ²).
 */
function primForm(t: Term, x: string): Term {
  const consts: Term[] = [];
  const core: Term[] = [];
  const walk = (f: Term): void => {
    if (f.k === 'mul') f.c.forEach(walk);
    else if (f.k === 'div' && !symbolsOf(f.b).has(x)) {
      consts.push(div(num(1), f.b));
      walk(f.a);
    } else if (!symbolsOf(f).has(x)) consts.push(f);
    else core.push(f);
  };
  if (t.k === 'add') return add(...t.c.map((c) => primForm(c, x)));
  walk(t);
  if (core.length === 1 && core[0]!.k === 'add')
    return add(...core[0]!.c.map((c) => primForm(consts.length ? mul(...consts, c) : c, x)));
  const fs = [...consts, ...core];
  return fs.length === 1 ? fs[0]! : mul(...fs);
}

function primitiveKind(F: Term, x: string): { kind: StepKind; label: string } | null {
  let tpl: { rules: StepRule[] } | null = null;
  try {
    tpl = withArcDerivatives(() => stepTemplate(primForm(F, x), x, () => null));
  } catch {
    tpl = null;
  }
  // une écriture qu'on ne sait pas lire comme une primitive usuelle : pas de pas
  if (!tpl) return null;
  if (tpl.rules.includes('product') || tpl.rules.includes('quotient')) return null;
  const r = tpl.rules.find((x) => x !== 'usual');
  if (!r) return { kind: 'primitive', label: STEP.primitive.label };
  const label =
    r === 'chain_exp'
      ? 'Reconnaître u′eᵘ'
      : r === 'chain_ln'
        ? 'Reconnaître u′ ÷ u'
        : r === 'chain_pow'
          ? 'Reconnaître u′uⁿ'
          : r === 'chain_sqrt'
            ? 'Reconnaître u′ ÷ √u'
            : r === 'chain_trig'
              ? 'Reconnaître u′ cos u'
              : r === 'inverse'
                ? 'Reconnaître u′ ÷ u²'
                : r === 'chain_arc'
                  ? arcLabel(F)
                  : STEP.recognize.label;
  return { kind: 'recognize', label };
}

/** moteur v60 : la forme à reconnaître d'une primitive en arctan ou arcsin */
function arcLabel(F: Term): string {
  const has = (t: Term, f: string): boolean =>
    t.k === 'fn'
      ? t.f === f || has(t.a, f)
      : t.k === 'add' || t.k === 'mul'
        ? t.c.some((c) => has(c, f))
        : t.k === 'num' || t.k === 'sym'
          ? false
          : has(t.a, f) || has(t.b, f);
  return has(F, 'arcsin') || has(F, 'arccos')
    ? 'Reconnaître u′ ÷ √(1 − u²)'
    : 'Reconnaître u′ ÷ (1 + u²)';
}

/** P = u × v avec u × v′ = la fonction : une intégration par parties */
function partsSplit(P: Term, target: Term, x: string, pts: number[]): { u: Term; v: Term } | null {
  let sc: { c: Term[]; core: Term[] };
  try {
    sc = splitConstant(P, x);
  } catch {
    return null;
  }
  const core: Term[] = [];
  const consts: Term[] = [...sc.c];
  // x × e^(2x) ÷ 2 : le quotient par une constante et les produits imbriqués sont dépliés
  const flat = (f: Term): void => {
    if (f.k === 'mul') f.c.forEach(flat);
    else if (f.k === 'div' && !symbolsOf(f.b).has(x)) {
      consts.push(div(num(1), f.b));
      flat(f.a);
    } else if (!symbolsOf(f).has(x)) consts.push(f);
    else core.push(f);
  };
  sc.core.forEach(flat);
  const n = core.length;
  if (!n || n > 6) return null;
  for (let mask = 1; mask < 1 << n; mask++) {
    const U = core.filter((_, i) => mask & (1 << i));
    const V = [...consts, ...core.filter((_, i) => !(mask & (1 << i)))];
    if (!V.some((f) => [...symbolsOf(f)].includes(x))) continue;
    const u = U.length === 1 ? U[0]! : mul(...U);
    const v = V.length === 1 ? V[0]! : mul(...V);
    const dv = safeDerive(v, x);
    if (!dv) continue;
    if (sameFn(mul(u, dv), target, x, pts)) return { u, v };
  }
  return null;
}

/** degré en cos et sin d'une écriture (cos⁴x : 4 ; (1 + cos 2x) ÷ 2 : 1) */
function trigDeg(t: Term): number {
  switch (t.k) {
    case 'fn':
      return t.f === 'cos' || t.f === 'sin' ? 1 : 0;
    case 'add':
      return Math.max(0, ...t.c.map(trigDeg));
    case 'mul':
      return t.c.reduce((s, c) => s + trigDeg(c), 0);
    case 'div':
      return trigDeg(t.a);
    case 'pow': {
      const n = t.b.k === 'num' && t.b.v.d === 1n ? Number(t.b.v.n) : 0;
      return n > 0 ? n * trigDeg(t.a) : 0;
    }
    default:
      return 0;
  }
}
/** nombre de fractions dont le dénominateur dépend de x (dans toute l'écriture) */
function fracCount(t: Term, x: string): number {
  switch (t.k) {
    case 'div':
      return (symbolsOf(t.b).has(x) ? 1 : 0) + fracCount(t.a, x) + fracCount(t.b, x);
    case 'add':
    case 'mul':
      return t.c.reduce((s, c) => s + fracCount(c, x), 0);
    case 'pow':
      return fracCount(t.a, x);
    case 'fn':
      return fracCount(t.a, x);
    default:
      return 0;
  }
}

function rewriteKind(old: Term, nw: Term, x: string): StepKind {
  const d0 = trigDeg(old);
  if (d0 >= 2 && trigDeg(nw) < d0) return 'linearize';
  let f0 = 0;
  let f1 = 0;
  try {
    f0 = fracCount(old, x);
    f1 = fracCount(nw, x);
  } catch {
    /* rien */
  }
  if (f0 === 1 && f1 >= 2) return 'decompose';
  return 'rewrite';
}

/** la fonction somme des intégrales (coef × intégrande) */
const sumFn = (items: Item[]): Term =>
  items.length === 1
    ? isOne(items[0]!.coef)
      ? items[0]!.atom!.body
      : mul(items[0]!.coef, items[0]!.atom!.body)
    : add(...items.map((it) => (isOne(it.coef) ? it.atom!.body : mul(it.coef, it.atom!.body))));

/** Le passage de l'écriture S à l'écriture T (de même valeur). */
function gesture(ks: KIntState, S: Form, T: Form): G {
  const x = ks.x;
  const d = diffForms(S, T);
  const constSame = near(S.cv, T.cv);
  const constWritten = shape(S.c) === shape(T.c);
  // rien d'autre que des calculs sur les nombres
  if (!d.gone.length && !d.came.length) {
    if (S.wkey === T.wkey) return { k: 'same' };
    const o0 = formOps(S);
    const o1 = formOps(T);
    if (o1 < o0) {
      if (formOps(S, true) - o1 > 4) return { k: 'jump' };
      return { k: 'step', steps: ['compute'], labels: [''] };
    }
    return { k: 'same' };
  }
  const goneI = d.gone.filter((it) => !it.atom);
  const cameI = d.came.filter((it) => !it.atom);
  const goneInt = d.gone.filter((it) => it.atom?.k === 'int');
  const goneBrk = d.gone.filter((it) => it.atom?.k === 'brk');
  const cameInt = d.came.filter((it) => it.atom?.k === 'int');
  const cameBrk = d.came.filter((it) => it.atom?.k === 'brk');

  // une intégrale ou un crochet remplacé par ce qu'on a calculé à part
  if (d.gone.length === 1 && d.gone[0]!.atom && !goneI.length) {
    const it = d.gone[0]!;
    for (const L of ks.side.get(it.atom!.key) ?? []) {
      const want = [...L.items.map((l) => ({ ...l, cv: l.cv * it.cv }))];
      const ok =
        want.length === d.came.length &&
        want.every((w) => d.came.some((c) => itemKey(c) === itemKey(w) && near(c.cv, w.cv))) &&
        near(T.cv, S.cv + it.cv * L.cv);
      if (ok) return { k: 'step', steps: ['replace'], labels: [''] };
    }
  }

  // reconnaître I : l'intégrale de départ réapparaît
  if (
    goneInt.length === 1 &&
    cameI.length === 1 &&
    d.gone.length === 1 &&
    d.came.length === 1 &&
    goneInt[0]!.atom!.key === ks.startAtom.key &&
    near(goneInt[0]!.cv, cameI[0]!.cv) &&
    constSame
  )
    return { k: 'step', steps: ['recognizeI'], labels: [''] };
  if (goneI.length || cameI.length) return { k: 'jump' };

  // crochets remplacés par leur valeur
  if (goneBrk.length && !goneInt.length && !d.came.length) {
    let sub: Term = S.c;
    const pieces: Term[] = isZeroT(S.c) ? [] : [S.c];
    for (const it of goneBrk) {
      const at = it.atom!;
      const F = at.body;
      const diffT = add(substitute(F, { [x]: at.hi }), mul(num(-1), substitute(F, { [x]: at.lo })));
      pieces.push(isOne(it.coef) ? diffT : mul(it.coef, diffT));
    }
    sub = pieces.length === 1 ? pieces[0]! : add(...pieces);
    const o0 = opsOf(trivial(sub));
    const o1 = opsOf(T.c);
    if ((o1 === 0 && o0 >= 4) || o0 - o1 > 7) return { k: 'jump' };
    return {
      k: 'step',
      steps: ['bracket'],
      labels: [goneBrk.length > 1 ? 'Remplacer x par les bornes (les crochets)' : ''],
    };
  }

  // crochets regroupés ou réécrits
  if (goneBrk.length && cameBrk.length && !goneInt.length && !cameInt.length) {
    const ref = goneBrk[0]!.atom!;
    if (![...goneBrk, ...cameBrk].every((it) => sameBounds(it.atom!, ref))) return { k: 'jump' };
    if (!constSame || !constWritten) return { k: 'jump' };
    const pts = pointsOf(ref.a, ref.b);
    const fOld = add(...goneBrk.map((it) => mul(it.coef, it.atom!.body)));
    const fNew = add(...cameBrk.map((it) => mul(it.coef, it.atom!.body)));
    if (!offsetFn(fOld, fNew, x, pts)) return { k: 'jump' };
    return {
      k: 'step',
      steps: ['bracketMerge'],
      labels: [cameBrk.length !== goneBrk.length ? '' : 'Transformer le crochet'],
    };
  }

  if (!goneInt.length || goneBrk.length) return { k: 'jump' };
  if (!constSame || !constWritten) {
    // une intégrale d'une constante calculée tout de suite
    if (!d.came.length && goneInt.every((it) => !symbolsOf(it.atom!.body).has(x)))
      return { k: 'step', steps: ['constant'], labels: [''] };
    return { k: 'jump' };
  }
  const ref = goneInt[0]!.atom!;
  const target = sumFn(goneInt);
  const pts = pointsOf(ref.a, ref.b);
  const allSame = (its: Item[]) => its.every((it) => sameBounds(it.atom!, ref));

  // Chasles : l'intervalle coupé en morceaux
  if (goneInt.length === 1 && cameInt.length >= 2 && !cameBrk.length) {
    const pieces = [...cameInt].sort(
      (p, q) => Math.min(p.atom!.a, p.atom!.b) - Math.min(q.atom!.a, q.atom!.b),
    );
    const lo = Math.min(ref.a, ref.b);
    const hi = Math.max(ref.a, ref.b);
    let cur = lo;
    let chain = true;
    for (const p of pieces) {
      const a = Math.min(p.atom!.a, p.atom!.b);
      const b = Math.max(p.atom!.a, p.atom!.b);
      if (!near(a, cur)) chain = false;
      cur = b;
    }
    if (chain && near(cur, hi) && !allSame(cameInt)) {
      const okPieces = pieces.every((p) => {
        const ppts = pointsOf(p.atom!.a, p.atom!.b);
        return sameFn(mul(p.coef, p.atom!.body), mul(goneInt[0]!.coef, ref.body), x, ppts);
      });
      if (okPieces) return { k: 'step', steps: ['chasles'], labels: [''] };
      return { k: 'jump' };
    }
  }
  // plusieurs primitives d'un coup : chaque intégrale devient un crochet (mêmes bornes)
  if (goneInt.length >= 2 && cameBrk.length === goneInt.length && !cameInt.length) {
    const left = [...cameBrk];
    const kinds: StepKind[] = [];
    const lbls: string[] = [];
    for (const it of goneInt) {
      const j = left.findIndex((B) => {
        if (!sameBounds(B.atom!, it.atom!)) return false;
        const dF = safeDerive(B.atom!.body, x);
        const ppts = pointsOf(it.atom!.a, it.atom!.b);
        return !!dF && sameFn(mul(B.coef, dF), mul(it.coef, it.atom!.body), x, ppts);
      });
      if (j < 0) return { k: 'jump' };
      const B = left.splice(j, 1)[0]!;
      if (!symbolsOf(it.atom!.body).has(x)) {
        kinds.push('constant');
        lbls.push('');
        continue;
      }
      const pk = primitiveKind(B.atom!.body, x);
      if (!pk)
        return {
          k: 'jump',
          message:
            'C’est bien une primitive, mais elle ne se lit pas directement : passe par une intégration par parties pour la trouver. Tu peux garder cette ligne sans HORA.',
        };
      kinds.push(pk.kind);
      lbls.push(pk.label);
    }
    return { k: 'step', steps: kinds, labels: lbls };
  }
  if (!allSame(goneInt) || !allSame(cameInt) || !allSame(cameBrk)) return { k: 'jump' };

  // une primitive : l'intégrale devient un crochet
  if (!cameInt.length && cameBrk.length === 1) {
    const B = cameBrk[0]!;
    const dF = safeDerive(B.atom!.body, x);
    if (!dF || !sameFn(mul(B.coef, dF), target, x, pts))
      return {
        k: 'jump',
        luck: true,
        message:
          'Ce crochet a la même valeur, mais ce n’est pas une primitive de la fonction : c’est un hasard. Dérive ce qui est dans le crochet pour vérifier.',
      };
    if (!symbolsOf(target).has(x)) return { k: 'step', steps: ['constant'], labels: [''] };
    const pk = primitiveKind(B.atom!.body, x);
    if (!pk)
      return {
        k: 'jump',
        message:
          'C’est bien une primitive, mais elle ne se lit pas directement : passe par une intégration par parties pour la trouver. Tu peux garder cette ligne sans HORA.',
      };
    return { k: 'step', steps: [pk.kind], labels: [pk.label] };
  }

  // intégration par parties : un crochet et une intégrale
  if (cameInt.length === 1 && cameBrk.length === 1) {
    const B = cameBrk[0]!;
    const C = cameInt[0]!;
    const dP = safeDerive(B.atom!.body, x);
    if (!dP) return { k: 'jump' };
    const lhs = add(mul(B.coef, dP), mul(C.coef, C.atom!.body));
    if (!sameFn(lhs, target, x, pts))
      return {
        k: 'jump',
        luck: true,
        message:
          'Ta ligne a la même valeur, mais ce n’est pas une intégration par parties juste : c’est un hasard. Vérifie u, v′, u′ et v.',
      };
    const perUnit = div(target, B.coef);
    if (goneInt.length === 1 && partsSplit(B.atom!.body, perUnit, x, pts))
      return { k: 'step', steps: ['parts'], labels: [''] };
    return {
      k: 'jump',
      message:
        'Ta ligne est juste, mais ce n’est pas une intégration par parties : choisis u et v′ avec u × v′ = la fonction à intégrer. Tu peux la garder sans HORA.',
    };
  }

  // seulement des intégrales : linéarité, linéariser, décomposer, transformer
  if (!cameBrk.length && cameInt.length) {
    const newFn = sumFn(cameInt);
    if (!sameFn(newFn, target, x, pts))
      return {
        k: 'jump',
        luck: true,
        message:
          'Les deux intégrales ont la même valeur, mais pas la même fonction : c’est un hasard. Vérifie ta transformation (linéarisation, développement, signe…).',
      };
    if (goneInt.length === 1 && cameInt.length === 1) {
      const g = goneInt[0]!;
      const c = cameInt[0]!;
      // la même fonction : une constante sort (ou rentre)
      if (keyOf(g.atom!.body) === keyOf(c.atom!.body)) return { k: 'same' };
      const ratio = (() => {
        const u = vec(g.atom!.body, x, pts);
        const v = vec(c.atom!.body, x, pts);
        let k: number | null = null;
        for (let i = 0; i < u.length; i++) {
          if (!Number.isFinite(u[i]!) || !Number.isFinite(v[i]!) || Math.abs(v[i]!) < 1e-12)
            continue;
          const r = u[i]! / v[i]!;
          if (k === null) k = r;
          else if (!closeTo(r, k)) return null;
        }
        return k;
      })();
      if (ratio !== null && !near(ratio, 1))
        return { k: 'step', steps: ['linearity'], labels: ['Sortir la constante'] };
      const kind = rewriteKind(g.atom!.body, c.atom!.body, x);
      return { k: 'step', steps: [kind], labels: [''] };
    }
    if (goneInt.length === 1 && cameInt.length >= 2) {
      const oldTerms = termsOf(target).map((t) => vec(t, x, pts));
      const newTerms = cameInt.map((it) => vec(mul(it.coef, it.atom!.body), x, pts));
      if (oldTerms.length >= cameInt.length && groupsByValues(oldTerms, newTerms))
        return { k: 'step', steps: ['linearity'], labels: [''] };
      const kind = rewriteKind(target, newFn, x);
      return {
        k: 'step',
        steps: [kind],
        labels: [kind === 'rewrite' ? 'Transformer et séparer' : ''],
      };
    }
    if (goneInt.length >= 2 && cameInt.length === 1)
      return { k: 'step', steps: ['linearity'], labels: ['Réunir les intégrales'] };
  }
  return { k: 'jump' };
}

/** chaque terme de `got` va dans un seul morceau de `want` */
function groupsByValues(got: number[][], want: number[][]): boolean {
  const n = got.length;
  const k = want.length;
  if (n > 10 || k > 5) return false;
  const width = got[0]?.length ?? 0;
  const sums = want.map(() => new Array<number>(width).fill(0));
  const assign = (i: number): boolean => {
    if (i === n) return sums.every((s, j) => sameVec(s, want[j]!));
    for (let j = 0; j < k; j++) {
      for (let p = 0; p < width; p++) sums[j]![p]! += got[i]![p]!;
      if (assign(i + 1)) return true;
      for (let p = 0; p < width; p++) sums[j]![p]! -= got[i]![p]!;
    }
    return false;
  };
  return assign(0);
}

/** Pourquoi le passage de S à T (faux) est faux : un message qui ne donne pas la valeur. */
function diagnose(ks: KIntState, S: Form, T: Form): { title: string; message: string } {
  const x = ks.x;
  const d = diffForms(S, T);
  const goneInt = d.gone.filter((it) => it.atom?.k === 'int');
  const goneBrk = d.gone.filter((it) => it.atom?.k === 'brk');
  const cameInt = d.came.filter((it) => it.atom?.k === 'int');
  const cameBrk = d.came.filter((it) => it.atom?.k === 'brk');
  if (goneInt.length === 1 && cameBrk.length === 1 && cameInt.length === 1) {
    const ref = goneInt[0]!.atom!;
    const pts = pointsOf(ref.a, ref.b);
    const target = sumFn(goneInt);
    const B = cameBrk[0]!;
    const C = cameInt[0]!;
    const dP = safeDerive(B.atom!.body, x);
    if (dP && sameFn(add(mul(B.coef, dP), mul(num(-1), C.coef, C.atom!.body)), target, x, pts))
      return {
        title: 'Le signe',
        message:
          'Intégration par parties : ∫u v′ = [u v] − ∫u′ v. Devant l’intégrale qui reste, c’est « − ».',
      };
    if (partsSplit(B.atom!.body, div(target, B.coef), x, pts))
      return {
        title: 'Vérifie u′ × v',
        message:
          'Le crochet [u v] est bon ; l’intégrale qui reste est celle de u′ × v : vérifie u′ et v.',
      };
    return {
      title: 'Ce n’est pas une intégration par parties',
      message: 'Choisis u et v′ avec u × v′ = la fonction à intégrer ; puis ∫u v′ = [u v] − ∫u′ v.',
    };
  }
  if (goneInt.length && cameBrk.length === 1 && !cameInt.length)
    return {
      title: 'Pas une primitive',
      message: 'Dérive ce qui est dans le crochet : tu dois retrouver la fonction à intégrer.',
    };
  if (goneBrk.length && !d.came.length) {
    let swapped = S.cv;
    for (const it of goneBrk) swapped += -it.cv * atomValue(ks, it.atom!);
    if (near(swapped, T.cv))
      return {
        title: 'Dans le bon ordre',
        message:
          '[F(x)](a ; b) vaut F(b) − F(a) : la borne du haut d’abord, puis on retranche celle du bas.',
      };
    return {
      title: 'Calcul faux',
      message: 'Remplace x par la borne du haut, puis par celle du bas, et retranche.',
    };
  }
  if (goneInt.length && cameInt.length && !cameBrk.length)
    return {
      title: 'Ce n’est pas la même intégrale',
      message:
        'La nouvelle écriture ne vaut pas la même chose : vérifie ta transformation (linéarisation, développement, signe…).',
    };
  if (!d.gone.length && !d.came.length)
    return { title: 'Calcul faux', message: 'Ce n’est pas la même valeur : reprends ton calcul.' };
  return { title: 'Ce n’est pas égal', message: 'Les deux membres n’ont pas la même valeur.' };
}

/* ------------------------------ juge ------------------------------ */

function knownPrefix(ks: KIntState, A: string[]): boolean {
  return ks.path.some((t) => {
    let atoms: string[];
    try {
      atoms = kintLineAtoms(t);
    } catch {
      return false;
    }
    return atoms.length > A.length && A.every((a, i) => atoms[i] === a);
  });
}

export function kintJudge(ks: KIntState, chips: Chip[], opts?: { verify?: boolean }): KIntVerdict {
  const raw = chips.flatMap((c) => c.atoms);
  const chipOf: number[] = [];
  chips.forEach((c, ci) => c.atoms.forEach(() => chipOf.push(ci)));
  const norm = kintNormalize(raw);
  const A = norm.atoms;
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const prefix = knownPrefix(ks, A);
  const incomplete = (): KIntVerdict =>
    opts?.verify
      ? { kind: 'syntax', atoms: A, title: 'Ligne inachevée', message: 'Termine ta ligne.' }
      : { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  const r = readLine(ks, A);
  if (r.k === 'empty') return { kind: 'empty', atoms: A, badChip: -1 };
  if (r.k === 'incomplete') return incomplete();
  if (r.k === 'syntax') {
    if (prefix && !opts?.verify) return { kind: 'building', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: r.title,
      message: r.message,
      badChip: chipOf[norm.src[r.index] ?? raw.length - 1] ?? chips.length - 1,
    };
  }
  if (r.k === 'noeq') {
    if (opts?.verify)
      return {
        kind: 'syntax',
        atoms: A,
        line: lineOf(A),
        title: 'Une égalité',
        message: `Écris une égalité : « ${ks.name} = … ».`,
      };
    return { kind: prefix ? 'building' : 'open', atoms: A, complete: false };
  }
  const base: Base = { atoms: A, line: lineOf(A) };
  const v = r.k === 'name' ? judgeName(ks, base, r.head, r.expr) : judgeChain(ks, base, r.members);
  if (prefix && !opts?.verify) {
    if (v.kind === 'step') return { ...v, kind: 'ready', complete: true };
    return { kind: 'building', atoms: A, complete: true };
  }
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return { ...v, complete: true };
}

/** les intégrales de la feuille (dans l'écriture de I la plus récente d'abord) */
function currentIntegrals(ks: KIntState): Atom[] {
  const out: Atom[] = [];
  const seen = new Set<string>();
  const forms = [...ks.classes.values()].flat().reverse();
  for (const f of [...forms, ...[...ks.side.values()].flat().reverse()])
    for (const it of f.items)
      if (it.atom?.k === 'int' && !seen.has(it.atom.key)) {
        seen.add(it.atom.key);
        out.push(it.atom);
      }
  return out;
}

/** les facteurs d'une écriture (une puissance entière compte autant de fois) */
function factorKeys(t: Term): string[] {
  const c = (() => {
    try {
      return canon(t);
    } catch {
      return t;
    }
  })();
  const fs = c.k === 'mul' ? c.c : [c];
  const out: string[] = [];
  for (const f of fs) {
    if (f.k === 'num') continue;
    if (f.k === 'pow' && f.b.k === 'num' && f.b.v.d === 1n && f.b.v.n > 0n && f.b.v.n < 6n)
      for (let i = 0; i < Number(f.b.v.n); i++) out.push(keyOf(f.a));
    else out.push(keyOf(f));
  }
  return out;
}
function isFactorOf(u: Term, g: Term): boolean {
  const fu = factorKeys(u);
  const fg = factorKeys(g);
  if (!fu.length) return false;
  const left = [...fg];
  for (const k of fu) {
    const i = left.indexOf(k);
    if (i < 0) return false;
    left.splice(i, 1);
  }
  return true;
}

function judgeName(ks: KIntState, base: Base, head: NameHead, e: Term): KIntVerdict {
  const x = ks.x;
  const text = kintText(base.atoms);
  const label = { u: 'Nommer u', dv: 'Nommer v′', du: 'Dériver u', v: 'Une primitive de v′' }[head];
  const move: IMove = { text, to: null, name: { head, expr: e } };
  const step = (): KIntVerdict => stepVerdict(base, ['name'], [label], move, false);
  const prev = ks.names[head];
  if (prev && keyOf(prev) === keyOf(e))
    return {
      kind: 'useless',
      ...base,
      title: 'Déjà écrit',
      message: 'Cette ligne est déjà sur ta feuille.',
    };
  if ([...symbolsOf(e)].some((s) => s !== x && s !== 'π'))
    return {
      kind: 'syntax',
      ...base,
      title: 'Une fonction de ' + x,
      message: `Écris une fonction de ${x} seulement.`,
    };
  const ints = currentIntegrals(ks);
  const iv = ints[0] ?? ks.startAtom;
  const pts = pointsOf(iv.a, iv.b);
  if (head === 'u' || head === 'dv') {
    if (head === 'u' && !symbolsOf(e).has(x))
      return {
        kind: 'unjustified',
        ...base,
        title: 'u dépend de ' + x,
        message: 'u est la partie qu’on dérive : choisis un facteur qui dépend de ' + x + '.',
      };
    const u = head === 'u' ? e : ks.names.u;
    if (head === 'dv' && u) {
      const ok = ints.some((g) => sameFn(mul(u, e), g.body, x, pointsOf(g.a, g.b)));
      if (ok) return step();
      return {
        kind: 'false',
        ...base,
        title: 'u × v′',
        message: 'u × v′ doit redonner la fonction à intégrer.',
      };
    }
    const ok = ints.some((g) => isFactorOf(e, g.body) || (head === 'dv' && keyOf(e) === '1'));
    if (ok) return step();
    return {
      kind: 'unjustified',
      ...base,
      title: 'Un facteur de la fonction',
      message: `${head === 'u' ? 'u' : 'v′'} est un des facteurs de la fonction à intégrer.`,
    };
  }
  if (head === 'du') {
    const u = ks.names.u;
    if (!u)
      return {
        kind: 'unjustified',
        ...base,
        title: 'Nomme d’abord u',
        message: 'Écris d’abord « u(x) = … ».',
      };
    const du = safeDerive(u, x);
    if (du && sameFn(du, e, x, pts)) return step();
    return {
      kind: 'false',
      ...base,
      title: 'Pas la dérivée de u',
      message: 'Dérive u : ce n’est pas sa dérivée.',
    };
  }
  const dv = ks.names.dv;
  if (!dv)
    return {
      kind: 'unjustified',
      ...base,
      title: 'Nomme d’abord v′',
      message: 'Écris d’abord « v′(x) = … ».',
    };
  const de = safeDerive(e, x);
  if (de && sameFn(de, dv, x, pts)) return step();
  return {
    kind: 'false',
    ...base,
    title: 'Pas une primitive de v′',
    message: 'Dérive v : tu dois retrouver v′.',
  };
}

function pureI(f: Form): number | null {
  if (f.items.length !== 1 || f.items[0]!.atom || !near(f.cv, 0) || !isZeroT(f.c)) return null;
  return f.items[0]!.cv;
}
function pureAtom(f: Form): Atom | null {
  if (f.items.length !== 1 || !f.items[0]!.atom || !isZeroT(f.c) || !isOne(f.items[0]!.coef))
    return null;
  return f.items[0]!.atom;
}
const allForms = (ks: KIntState): Form[] => [
  ...[...ks.classes.values()].flat(),
  ...[...ks.side.values()].flat(),
];

function judgeChain(ks: KIntState, base: Base, members: Form[]): KIntVerdict {
  const text = kintText(base.atoms);
  if (ks.givenTexts.has(text))
    return {
      kind: 'useless',
      ...base,
      title: 'C’est l’énoncé',
      message: 'Cette ligne est déjà dans l’énoncé.',
    };
  const vals = members.map((m) => formValue(ks, m));
  if (vals.some((v) => !Number.isFinite(v)))
    return {
      kind: 'false',
      ...base,
      title: 'Pas de valeur',
      message:
        'Une intégrale ou un crochet n’a pas de valeur ici : la fonction n’est pas définie sur tout l’intervalle.',
    };
  // la source du premier membre
  const k0 = pureI(members[0]!);
  const a0 = k0 === null ? pureAtom(members[0]!) : null;
  if (k0 === null && !a0)
    return {
      kind: 'unjustified',
      ...base,
      title: `Pars de ${ks.name}`,
      message: `Écris « ${ks.name} = … » (ou une intégrale de ta feuille, « ∫(a ; b) … dx = … »).`,
    };
  if (a0 && !allForms(ks).some((f) => f.items.some((it) => it.atom?.key === a0.key)))
    return {
      kind: 'unjustified',
      ...base,
      title: 'Pas dans ton calcul',
      message: `Cette intégrale n’apparaît pas encore dans le calcul de ${ks.name}.`,
    };
  // vrai ?
  const iVal = ks.value;
  const want = k0 !== null ? k0 * iVal : atomValue(ks, a0!);
  for (let i = 1; i < members.length; i++) {
    if (!near(vals[i]!, want)) {
      const S = i === 1 ? bestSource(ks, members[0]!, k0, a0, members[1]!) : members[i - 1]!;
      return trapOr(ks, base, diagnose(ks, S, members[i]!));
    }
  }
  const to: IMove['to'] =
    k0 !== null
      ? { k: 'class', key: classKey(k0), forms: members.slice(1) }
      : { k: 'side', key: a0!.key, forms: members.slice(1) };
  const move: IMove = { text, to };
  const steps: StepKind[] = [];
  const labels: string[] = [];
  let jump: string | undefined | null = null;
  let luck = false;
  let same = 0;
  for (let i = 1; i < members.length; i++) {
    const T = members[i]!;
    const g = i === 1 ? firstGesture(ks, members[0]!, k0, a0, T) : gesture(ks, members[i - 1]!, T);
    if (g.k === 'step') {
      steps.push(...g.steps);
      labels.push(...g.labels);
    } else if (g.k === 'same') same++;
    else {
      jump = g.message ?? jump ?? undefined;
      if (g.luck) luck = true;
    }
  }
  const last = members[members.length - 1]!;
  const final =
    !ks.concluded &&
    k0 !== null &&
    near(k0, 1) &&
    !last.items.length &&
    near(last.cv, ks.answerValue) &&
    opsOf(last.c) <= ks.answerOps;
  if (luck)
    // gardée, mais elle ne sert pas de départ : la suite partirait d'une fonction fausse
    return {
      ...jumpVerdict(base, { text, to: null }, false, jump ?? undefined),
      title: 'Même valeur par hasard',
    };
  if (jump !== null) return jumpVerdict(base, move, final, jump);
  if (!steps.length)
    return {
      kind: 'useless',
      ...base,
      title: same ? 'Rien de nouveau' : 'Juste, mais ça ne t’avance pas',
      message: same
        ? 'Cette écriture est déjà sur ta feuille.'
        : 'Les deux écritures sont égales, mais le calcul n’avance pas.',
      imove: move,
      final,
    };
  return stepVerdict(base, steps, labels, move, final);
}

/** les écritures d'où part le premier membre */
function sourcesOf(ks: KIntState, k0: number | null, a0: Atom | null): Form[] {
  if (k0 !== null) return [...(ks.classes.get(classKey(k0)) ?? [])].reverse();
  const own: Form = {
    items: [{ atom: a0!, coef: num(1), cv: 1 }],
    c: num(0),
    cv: 0,
    iCoef: 0,
    wkey: `${a0!.key}:1#`,
  };
  return [own, ...[...(ks.side.get(a0!.key) ?? [])].reverse()];
}

function bestSource(ks: KIntState, m0: Form, k0: number | null, a0: Atom | null, T: Form): Form {
  const src = sourcesOf(ks, k0, a0);
  // la plus proche : celle qui partage le plus d'intégrales avec T
  let best = src[0] ?? m0;
  let score = -1;
  for (const S of src) {
    const d = diffForms(S, T);
    const s = S.items.length - d.gone.length;
    if (s > score) {
      score = s;
      best = S;
    }
  }
  return best;
}

/** le premier passage : depuis les écritures établies de k·I (ou de l'intégrale) */
function firstGesture(ks: KIntState, m0: Form, k0: number | null, a0: Atom | null, T: Form): G {
  const src = sourcesOf(ks, k0, a0);
  let fallback: G | null = null;
  for (const S of src) {
    const g = gesture(ks, S, T);
    if (g.k === 'step') return g;
    if (g.k === 'same') fallback = g;
    else if (!fallback) fallback = g;
  }
  if (k0 !== null && !near(k0, 1) && !ks.classes.has(classKey(k0))) {
    // regrouper les I : I = X + cI devient (1 − c)I = X
    for (const F of [...(ks.classes.get(classKey(1)) ?? [])].reverse()) {
      if (near(F.iCoef, 0) || !near(1 - F.iCoef, k0)) continue;
      const X: Form = { ...F, items: F.items.filter((it) => it.atom) };
      X.wkey =
        X.items
          .map((it) => `${itemKey(it)}:${shape(it.coef)}`)
          .sort()
          .join(',') +
        '#' +
        (isZeroT(X.c) ? '' : shape(X.c));
      const g = gesture(ks, X, T);
      if (g.k === 'same') return { k: 'step', steps: ['collect'], labels: [''] };
      if (g.k === 'step')
        return { k: 'step', steps: ['collect', ...g.steps], labels: ['', ...g.labels] };
    }
  }
  if (k0 !== null && near(k0, 1)) {
    // diviser : kI = G donne I = G ÷ k
    for (const [key, forms] of ks.classes) {
      if (key === classKey(1)) continue;
      const k = Number(key.slice(2)) / 1e9;
      for (const G of [...forms].reverse()) {
        if (G.items.length !== T.items.length) continue;
        const ok = G.items.every((gi) =>
          T.items.some((ti) => itemKey(ti) === itemKey(gi) && near(ti.cv, gi.cv / k)),
        );
        if (!ok || !near(T.cv, G.cv / k)) continue;
        if (formOps(T) > formOps(G) + 2) continue;
        return { k: 'step', steps: ['divide'], labels: [`Diviser par ${formatK(k)}`] };
      }
    }
  }
  return fallback ?? { k: 'jump' };
}

function formatK(k: number): string {
  if (Number.isInteger(k)) return String(k).replace('-', '−');
  return String(Math.round(k * 1000) / 1000)
    .replace('.', ',')
    .replace('-', '−');
}

/* ------------------------------ mise à jour ------------------------------ */

export function kintCommit(ks: KIntState, v: KIntVerdict): void {
  const m = v.imove;
  if (!m) return;
  ks.written.add(m.text);
  ks.lines++;
  if (m.name) {
    const { head, expr } = m.name;
    // une nouvelle u : sa dérivée est à refaire ; un nouveau v′ : sa primitive aussi
    if (head === 'u' && ks.names.u && keyOf(ks.names.u) !== keyOf(expr)) delete ks.names.du;
    if (head === 'dv' && ks.names.dv && keyOf(ks.names.dv) !== keyOf(expr)) delete ks.names.v;
    ks.names[head] = expr;
    return;
  }
  if (m.to) {
    const map = m.to.k === 'class' ? ks.classes : ks.side;
    map.set(m.to.key, [...(map.get(m.to.key) ?? []), ...m.to.forms]);
  }
  if (m.final) ks.concluded = true;
  else if (m.to?.k === 'class' && m.to.key === classKey(1)) {
    const last = m.to.forms[m.to.forms.length - 1];
    if (
      last &&
      !last.items.length &&
      near(last.cv, ks.answerValue) &&
      opsOf(last.c) <= ks.answerOps
    )
      ks.concluded = true;
  }
}

export function kintGoalReached(ks: KIntState): boolean {
  return ks.concluded;
}

/**
 * L'intégrale de départ a-t-elle sur la feuille une écriture sans intégrale ni crochet (sa
 * valeur calculée) ? Sert aux sommes de Riemann (lot 6.64), qui calculent l'intégrale
 * comme une feuille d'intégrales à part.
 */
export function kintStartComputed(ks: KIntState): boolean {
  const forms = [...(ks.classes.get(classKey(1)) ?? []), ...(ks.side.get(ks.startAtom.key) ?? [])];
  return forms.some((f) => !f.items.length);
}

/** une ligne (texte) lue : ses membres (null : illisible ou une ligne qui nomme) */
function membersOf(ks: KIntState, text: string): Form[] | null {
  let A: string[];
  try {
    A = kintLineAtoms(text);
  } catch {
    return null;
  }
  const r = readLine(ks, A);
  return r.k === 'chain' ? r.members : null;
}

/** la ligne est vraie (I vaut sa valeur) */
export function kintHolds(ks: KIntState, text: string): boolean {
  const ms = membersOf(ks, text);
  if (!ms || ms.length < 2) return false;
  const v0 = formValue(ks, ms[0]!);
  return Number.isFinite(v0) && ms.every((m) => near(formValue(ks, m), v0));
}

/** la ligne donne la valeur de I */
export function kintIsAnswer(ks: KIntState, text: string): boolean {
  const ms = membersOf(ks, text);
  if (!ms || ms.length < 2 || pureI(ms[0]!) === null || !near(pureI(ms[0]!)!, 1)) return false;
  const last = ms[ms.length - 1]!;
  return !last.items.length && near(last.cv, ks.answerValue);
}

/** une ligne déjà établie (le coup de pouce passe à la suivante) */
export function kintEstablished(ks: KIntState, text: string): boolean {
  let A: string[];
  try {
    A = kintLineAtoms(text);
  } catch {
    return false;
  }
  if (ks.written.has(kintText(A))) return true;
  const r = readLine(ks, A);
  if (r.k === 'name') {
    const n = ks.names[r.head];
    return !!n && keyOf(n) === keyOf(r.expr);
  }
  if (r.k !== 'chain') return false;
  const k0 = pureI(r.members[0]!);
  const a0 = k0 === null ? pureAtom(r.members[0]!) : null;
  const pool =
    k0 !== null ? (ks.classes.get(classKey(k0)) ?? []) : a0 ? (ks.side.get(a0.key) ?? []) : null;
  if (!pool) return false;
  const keys = new Set(pool.map((f) => f.wkey));
  return r.members.slice(1).every((m) => keys.has(m.wkey));
}

const chipsOf = (atoms: string[]): Chip[] => atoms.map((a) => ({ atoms: [a], text: a }));

/** coup de pouce : la ligne du corrigé qui suit la dernière déjà écrite */
export function kintNextMove(
  ks: KIntState,
): { line: Line; text: string; atoms: string[]; label: string } | null {
  if (ks.concluded) return null;
  let from = 0;
  ks.path.forEach((t, i) => {
    if (kintEstablished(ks, t)) from = i + 1;
  });
  for (let i = from; i < ks.path.length; i++) {
    const t = ks.path[i]!;
    if (kintEstablished(ks, t)) continue;
    const atoms = kintLineAtoms(t);
    const v = kintJudge(ks, chipsOf(atoms), { verify: true });
    return { line: kintLine(t), text: kintText(atoms), atoms, label: v.rule?.label ?? '' };
  }
  return null;
}

export function kintSolvePath(model: ScratchModel): {
  lines: { line: Line; text: string; atoms: string[]; v: KIntVerdict }[];
  reached: boolean;
} {
  const ks = createKIntState(model);
  const lines: { line: Line; text: string; atoms: string[]; v: KIntVerdict }[] = [];
  for (const t of ks.path) {
    const atoms = kintLineAtoms(t);
    const v = kintJudge(ks, chipsOf(atoms), { verify: true });
    if (v.kind !== 'step') break;
    kintCommit(ks, v);
    lines.push({ line: kintLine(t), text: kintText(atoms), atoms, v });
  }
  return { lines, reached: ks.concluded };
}

/* ------------------------------ palette ------------------------------ */

/** les opérations des intégrales, dans l'ordre d'affichage */
export const INTEGRAL_OPS = [
  '+',
  '−',
  '×',
  '÷',
  '(',
  ')',
  '²',
  '^',
  '√',
  'exp',
  'ln',
  'cos',
  'sin',
  'tan',
  'π',
  '=',
];

/**
 * Les bouts (D6 : l'énoncé et le corrigé, jamais les pièges ni les choix) : I, x, u(x)…,
 * les signes « ∫(a ; b) », « dx », « [ », « ](a ; b) » des bornes utilisées, les nombres,
 * les opérations utiles.
 */
export function kintPaletteParts(model: ScratchModel): {
  names: string[];
  signs: string[];
  numbers: string[];
  ops: string[];
} {
  const spec = model.kernel!;
  const texts = [spec.start, ...(spec.path ?? []), spec.answer];
  const atoms = texts.flatMap((t) => {
    try {
      return kintLineAtoms(t);
    } catch {
      return [];
    }
  });
  const used = new Set(atoms);
  const x = (() => {
    try {
      return integralQuestionOf(model).x;
    } catch {
      return 'x';
    }
  })();
  const names = [
    spec.goal.target,
    x,
    ...['u', 'u′', 'v′', 'v'].map((h) => `${h}(${x})`).filter((n) => used.has(n)),
    ...(used.has('e') ? ['e'] : []),
  ];
  const ints = [...new Set(atoms.filter(isIntegralAtom))];
  const closes = [...new Set(atoms.filter(isBracketClose))];
  const signs = [...ints, `d${x}`, ...(closes.length ? ['[', ...closes] : [])];
  const nums = [...new Set(atoms.filter((a) => /^\d+(,\d+)?$/.test(a)))];
  const value = (s: string) => Number(s.replace(',', '.'));
  nums.sort((a, b) => value(a) - value(b) || a.localeCompare(b));
  const always = new Set(['+', '−', '×', '÷', '(', ')', '=']);
  const ops = INTEGRAL_OPS.filter((o) => always.has(o) || used.has(o));
  const sups = [...new Set(atoms.filter((a) => /^[⁰¹³⁴⁵⁶⁷⁸⁹]+$/.test(a)))];
  if (sups.length)
    ops.splice(ops.indexOf('²') >= 0 ? ops.indexOf('²') + 1 : ops.indexOf('='), 0, ...sups);
  return { names, signs, numbers: nums, ops };
}

/** les bouts d'une ligne de l'énoncé (affichage, recopie) */
export function kintGivenAtoms(text: string): string[] {
  return kintLineAtoms(text);
}
